<template>
  <div class="s-setting-container">
    <div class="main-header">
      <div>Branding</div>
      <SaveCancelSection
        v-if="adminRole === 'admin'"
        :cancel="resetPartnerConfig"
        :save="updatePartnerConfig"
        :disabled="!changeCheck"
      />
    </div>
    <Branding
      :currentCorporation="currentCorporation"
      :primaryColor="primaryColor"
      :logoPreview="logoPreview"
      :cropperPreview="cropperPreview"
      :appIconPreview="appIconPreview"
      :appName="appName"
      :onChange="handleChange"
      :selectedImage="selectedImage"
      :handleEdit="handleEdit"
      :showCustomApp="showCustomApp"
      :errorObj="errorObj"
    />
    <ProcessOverlay
      :show="updating"
      text="Saving"
    />
  </div>
</template>

<script>
import partnerSettingsMixin from '../../../mixins/partnerSettingsMixin';

import Branding from '../../partnerSettings/Branding.vue';
import SaveCancelSection from '../shared/SaveCancelSection.vue';
import ProcessOverlay from '../../ProcessOverlay.vue';

import stepScaleImage from '../../../lib/general/stepScaleImage';
import dataURLtoFile from '../../../lib/general/dataURLtoFile';

export default {
  name: 'BrandingSection',
  components: {
    Branding,
    ProcessOverlay,
    SaveCancelSection,
  },
  mixins: [partnerSettingsMixin],
  props: {
    currentCorporation: Object,
    partnerSettings: Object,
    update: Function,
    uploadLogo: Function,
  },
  data() {
    const {
      LOGO_URL = '',
      PRIMARY_COLOR = '#F3F4F6',
      CUSTOM_APP_ICON_URL = '',
      CUSTOM_APP_NAME = '',
      SHOW_CUSTOM_APP_SETUP = false,
    } = this.partnerSettings;
    return {
      primaryColor: PRIMARY_COLOR,
      logoFormData: null,
      logoPreview: LOGO_URL,
      cropperPreview: '',
      appIconFormData: null,
      appIconPreview: CUSTOM_APP_ICON_URL,
      appName: CUSTOM_APP_NAME,
      showCustomApp: SHOW_CUSTOM_APP_SETUP,
      updating: false,
      errorObj: {},
    };
  },
  computed: {
    changeCheck() {
      const {
        PRIMARY_COLOR = '#F3F4F6',
        CUSTOM_APP_NAME = '',
        SHOW_CUSTOM_APP_SETUP = false,
      } = this.partnerSettings;
      let changes = false;
      if (PRIMARY_COLOR !== this.primaryColor) {
        changes = true;
      }
      if (this.logoFormData) {
        changes = true;
      }
      if (this.appIconFormData) {
        changes = true;
      }
      if (CUSTOM_APP_NAME !== this.appName) {
        changes = true;
      }
      if (SHOW_CUSTOM_APP_SETUP !== this.showCustomApp) {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      return changes;
    },
  },
  mounted() {
    const {
      LOGO_URL,
    } = this.partnerSettings;
    if (LOGO_URL) {
      this.logoPreview = LOGO_URL;
    }
  },
  methods: {
    loadImage(field, imageUrl) {
      this.logoPreview = imageUrl;
    },
    selectedImage(type, files) {
      const image = files[0];
      const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.svg)$/i;
      if (image) {
        const errorObj = {};
        if (type === 'primary' && !allowedExtensions.exec(image.name)) {
          errorObj.logoErr = 'Please make sure you\'re uploading\n.jpg, .png or .svg file\nClick to try again';
        }
        if (type === 'appIcon' && !allowedExtensions.exec(image.name)) {
          errorObj.appIconErr = 'Please make sure you\'re uploading\n.jpg or .png file\nClick to try again';
        }
        this.errorObj = errorObj;
        if (Object.keys(errorObj).length === 0) {
          const reader = new FileReader();
          reader.readAsDataURL(image);
          reader.onload = (e) => {
            const {
              result,
            } = e.target;
            const img = new Image();
            img.src = result;
            img.onload = () => {
              const formData = new FormData();
              if (type === 'primary') {
                formData.append('logoType', type);
                formData.append('logo', image);
                this.logoFormData = formData;
                const scaledImage = stepScaleImage(img, 0.5);
                this.logoPreview = scaledImage;
                this.cropperPreview = scaledImage;
              } else if (type === 'appIcon') {
                const width = img.naturalWidth;
                const height = img.naturalHeight;
                if (width !== 1024 || height !== 1024) {
                  this.errorObj = {
                    appIconErr: 'Image size must be\n1024px by 1024px',
                  };
                  this.appIconPreview = null;
                  this.appIconFormData = null;
                } else {
                  formData.append('logoType', type);
                  formData.append('logo', image);
                  this.appIconFormData = formData;
                  this.appIconPreview = result;
                }
              }
            };
          };
        }
      }
    },
    async handleEdit() {
      const logo = await dataURLtoFile(this.logoPreview, 'logo.png');
      this.selectedImage('primary', [logo]);
    },
    async handleChange(field, value) {
      this[field] = value;
      if (field === 'logoPreview' && value) {
        const formData = new FormData();
        const logo = await dataURLtoFile(value, 'logo');
        formData.append('logoType', 'primary');
        formData.append('logo', logo);
        this.logoFormData = formData;
      }
    },
    resetPartnerConfig() {
      const {
        LOGO_URL = '',
        PRIMARY_COLOR = '#F3F4F6',
        CUSTOM_APP_ICON_URL = '',
        CUSTOM_APP_NAME = '',
        SHOW_CUSTOM_APP_SETUP = false,
      } = this.partnerSettings;
      this.primaryColor = PRIMARY_COLOR;
      this.logoFormData = null;
      this.logoPreview = LOGO_URL;
      this.cropperPreview = '';
      this.appIconFormData = null;
      this.appIconPreview = CUSTOM_APP_ICON_URL;
      this.appName = CUSTOM_APP_NAME;
      this.showCustomApp = SHOW_CUSTOM_APP_SETUP;
      this.updating = false;
    },
    async updatePartnerConfig() {
      const {
        primaryColor,
        appName,
        showCustomApp,
      } = this;
      this.changePartnerSettingsValue('showWarningOnExit', false);
      this.updating = true;
      await this.update({
        PRIMARY_COLOR: primaryColor,
        CUSTOM_APP_NAME: appName,
        SHOW_CUSTOM_APP_SETUP: showCustomApp,
      });
      if (this.logoFormData) {
        this.logoFormData.corpId = this.currentCorporation.id;
        await this.$store.dispatch('updateCorporationLogo', this.logoFormData);
        this.logoFormData = null;
        this.cropperPreview = '';
      }
      if (this.appIconFormData) {
        this.appIconFormData.corpId = this.currentCorporation.id;
        await this.$store.dispatch('updateCorporationLogo', this.appIconFormData);
        this.appIconFormData = null;
      }
      this.updating = false;
    },
  },
};
</script>
